import { UserRights } from '@work4all/models/lib/additionalEnums/UserRights';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ModuleAccessRightType } from '@work4all/models/lib/Enums/ModuleAccessRightType.enum';

import {
  PermissionRequiredRight,
  PermissionsRequiredRightsByEntity,
} from '../types';

// ==========================================
// Common Permission Configurations
// ==========================================

const COMMON_ERP_MAR_LIST: (
  | PermissionRequiredRight
  | PermissionRequiredRight[]
)[] = [
  {
    type: 'ModuleAccessRight',
    value: ModuleAccessRightType.FEATURE_WORK_4_ALL_WEB_FULL,
  },
  {
    type: 'ModuleAccessRight',
    value: ModuleAccessRightType.FEATURE_ERP_LIGHT,
    mustNotHave: true,
  },
  [
    {
      type: 'ModuleAccessRight',
      value: ModuleAccessRightType.FEATURE_WORK_4_ALL_WEB_FULL,
    },
    {
      type: 'ModuleAccessRight',
      value: ModuleAccessRightType.FEATURE_ERP_LIGHT,
    },
  ],
];

const DELIVERY_NOTE_MAR_LIST: PermissionRequiredRight[] = [
  {
    type: 'ModuleAccessRight',
    value: ModuleAccessRightType.FEATURE_WORK_4_ALL_WEB_FULL,
  },
  {
    type: 'ModuleAccessRight',
    value: ModuleAccessRightType.FEATURE_ERP_LIGHT,
  },
];

const DELIVERY_NOTE_CAN_CONVERT_MAR_LIST: (
  | PermissionRequiredRight
  | PermissionRequiredRight[]
)[] = [
  DELIVERY_NOTE_MAR_LIST,
  /*
   * Light user in the delivery note can do anything normally except converting
   *
   * Full context: https://work4all.atlassian.net/browse/WW-4148
   */
  {
    type: 'ModuleAccessRight',
    value: ModuleAccessRightType.FEATURE_WORK_4_ALL_WEB_FULL,
  },
  {
    type: 'ModuleAccessRight',
    value: ModuleAccessRightType.FEATURE_ERP_LIGHT,
    mustNotHave: true,
  },
];

const CONTRACT_LIGHT_USER_MAR_LIST: (
  | PermissionRequiredRight
  | PermissionRequiredRight[]
)[] = [
  {
    type: 'ModuleAccessRight',
    value: ModuleAccessRightType.FEATURE_WORK_4_ALL_WEB_FULL,
  },
  {
    type: 'ModuleAccessRight',
    value: ModuleAccessRightType.FEATURE_ERP_LIGHT,
    mustNotHave: true,
  },
];

// ==========================================
// ERP Permission Configurations
// ==========================================

export const ERP_PERMISSIONS_REQUIRED_RIGHTS: PermissionsRequiredRightsByEntity =
  {
    // Calculation
    [Entities.calculation]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.KalkulationenBearbeiten },
        ...COMMON_ERP_MAR_LIST,
      ],
      canEdit: [
        {
          type: 'UserRightOperation',
          value: UserRights.KalkulationenBearbeiten,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
      canDelete: [
        { type: 'UserRightOperation', value: UserRights.KalkulationenLoeschen },
        ...COMMON_ERP_MAR_LIST,
      ],
      canConvert: [
        { type: 'UserRight', value: UserRights.KalkulationenVerdoppeln },
        ...COMMON_ERP_MAR_LIST,
      ],
    },

    // Offer
    [Entities.offer]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.AngebotErstellenAendern },
        ...COMMON_ERP_MAR_LIST,
      ],
      canEdit: [
        {
          type: 'UserRightOperation',
          value: UserRights.AngebotErstellenAendern,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
      canDelete: [
        { type: 'UserRightOperation', value: UserRights.AngebotLoeschen },
        ...COMMON_ERP_MAR_LIST,
      ],
      canConvert: [
        { type: 'UserRight', value: UserRights.AngeboteVerdoppeln },
        ...COMMON_ERP_MAR_LIST,
      ],
    },

    // Contract
    [Entities.contract]: {
      canAdd: [
        {
          type: 'UserRight',
          value: UserRights.AuftragsbestaetigungErstellenAendern,
        },
        ...CONTRACT_LIGHT_USER_MAR_LIST,
      ],
      canEdit: [
        {
          type: 'UserRight',
          value: UserRights.AuftragsbestaetigungErstellenAendern,
        },
        ...CONTRACT_LIGHT_USER_MAR_LIST,
      ],
      canDelete: [
        { type: 'UserRightOperation', value: UserRights.AuftraegeLoeschen },
        ...CONTRACT_LIGHT_USER_MAR_LIST,
      ],
      canConvert: [
        { type: 'UserRight', value: UserRights.AuftraegeVerdoppeln },
        /*
         * Light user can convert Contract to Delivery Note
         *
         * Full context: https://work4all.atlassian.net/browse/WW-6286
         */
        [
          {
            type: 'ModuleAccessRight',
            value: ModuleAccessRightType.FEATURE_WORK_4_ALL_WEB_FULL,
          },
          {
            type: 'ModuleAccessRight',
            value: ModuleAccessRightType.FEATURE_ERP_LIGHT,
          },
        ],
      ],
    },

    // Invoice
    [Entities.invoice]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.RechnungErstellenAendern },
        ...COMMON_ERP_MAR_LIST,
      ],
      canEdit: [
        {
          type: 'UserRightOperation',
          value: UserRights.RechnungErstellenAendern,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
      canDelete: [
        { type: 'UserRightOperation', value: UserRights.RechnungenLoeschen },
        ...COMMON_ERP_MAR_LIST,
      ],
      canConvert: [
        { type: 'UserRight', value: UserRights.RechnungenVerdoppeln },
        ...COMMON_ERP_MAR_LIST,
      ],
    },

    // Delivery Note
    [Entities.deliveryNote]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.LieferscheinErstellenAendern },
        DELIVERY_NOTE_MAR_LIST,
      ],
      canEdit: [
        {
          type: 'UserRightOperation',
          value: UserRights.LieferscheinErstellenAendern,
        },
        DELIVERY_NOTE_MAR_LIST,
      ],
      canDelete: [
        { type: 'UserRightOperation', value: UserRights.LieferscheineLoeschen },
        DELIVERY_NOTE_MAR_LIST,
      ],
      canConvert: [
        { type: 'UserRight', value: UserRights.LieferscheineVerdoppeln },
        ...DELIVERY_NOTE_CAN_CONVERT_MAR_LIST,
      ],
    },

    // Demand
    [Entities.demand]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.BedarfErstellenAendern },
        ...COMMON_ERP_MAR_LIST,
      ],
      canEdit: [
        { type: 'UserRight', value: UserRights.BedarfErstellenAendern },
        ...COMMON_ERP_MAR_LIST,
      ],
      canDelete: [
        {
          type: 'UserRightOperation',
          value: UserRights.BedarfsmeldungenLoeschen,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
      canConvert: [
        { type: 'UserRight', value: UserRights.BedarfsanforderungenVerdoppeln },
        ...COMMON_ERP_MAR_LIST,
      ],
    },

    // Order
    [Entities.order]: {
      canAdd: [
        { type: 'UserRight', value: UserRights.BestellungErstellenAendern },
        ...COMMON_ERP_MAR_LIST,
      ],
      canEdit: [
        {
          type: 'UserRightOperation',
          value: UserRights.BestellungErstellenAendern,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
      canDelete: [
        { type: 'UserRightOperation', value: UserRights.BestellungenLoeschen },
        ...COMMON_ERP_MAR_LIST,
      ],
      canConvert: [
        { type: 'UserRight', value: UserRights.BestellungenVerdoppeln },
        ...COMMON_ERP_MAR_LIST,
      ],
    },

    // Inbound Delivery Note
    [Entities.inboundDeliveryNote]: {
      canAdd: [
        {
          type: 'UserRight',
          value: UserRights.EingangslieferscheinAnlegenAendern,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
      canEdit: [
        {
          type: 'UserRightOperation',
          value: UserRights.EingangslieferscheinAnlegenAendern,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
      canDelete: [
        {
          type: 'UserRightOperation',
          value: UserRights.EingangslieferscheinLoeschen,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
      canConvert: [
        {
          type: 'UserRight',
          value: UserRights.EingangslieferscheineVerdoppeln,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
    },

    // Inbound Invoice
    [Entities.inboundInvoice]: {
      canAdd: [
        {
          type: 'UserRight',
          value: UserRights.EingangsrechnungNurAnschauen,
          mustNotHave: true,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
      canEdit: [
        {
          type: 'UserRightOperation',
          value: UserRights.EingangsrechnungNurAnschauen,
          mustNotHave: true,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
      canDelete: [
        {
          type: 'UserRightOperation',
          value: UserRights.EingangsrechnungenLoeschen,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
    },

    // Review Model
    [Entities.reViewModel]: {
      canAdd: [
        {
          type: 'UserRight',
          value: UserRights.EingangsrechnungNurAnschauen,
          mustNotHave: true,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
      canEdit: [
        {
          type: 'UserRightOperation',
          value: UserRights.EingangsrechnungNurAnschauen,
          mustNotHave: true,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
      canDelete: [
        {
          type: 'UserRightOperation',
          value: UserRights.EingangsrechnungenLoeschen,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
    },

    // Service Contract
    [Entities.serviceContract]: {
      canAdd: [
        {
          type: 'UserRight',
          value: UserRights.WartungsvertraegeAnlegenundAendern,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
      canEdit: [
        {
          type: 'UserRight',
          value: UserRights.WartungsvertraegeAnlegenundAendern,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
      canDelete: [
        {
          type: 'UserRight',
          value: UserRights.WartungsvertraegeAnlegenundAendern,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
    },

    // Batch Billing Service Contracts Request
    [Entities.batchBillingServiceContractsRequest]: {
      canAdd: [
        {
          type: 'UserRight',
          value: UserRights.WartungsvertraegeAboLaufAusfuehren,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
      canEdit: [
        {
          type: 'UserRight',
          value: UserRights.WartungsvertraegeAboLaufAusfuehren,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
      canDelete: [
        {
          type: 'UserRight',
          value: UserRights.WartungsvertraegeAboLaufAusfuehren,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
    },

    // RA
    [Entities.ra]: {
      canDelete: [
        {
          type: 'UserRightOperation',
          value: UserRights.RechnungsausgangsbuchEinsehenBearbeiten,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
    },

    // RA View Model
    [Entities.raViewModel]: {
      canDelete: [
        {
          type: 'UserRightOperation',
          value: UserRights.RechnungsausgangsbuchEinsehenBearbeiten,
        },
        ...COMMON_ERP_MAR_LIST,
      ],
    },
  };
